/**
 * Keyboard shortcuts: one table drives both the handler and the "?" dialog. Mod is Cmd on macOS, Ctrl elsewhere.
 * Shortcuts are ignored while typing in a text field (except Escape), so inspector fields keep their own undo, and
 * while a modal dialog is open.
 */
import {
  addElement,
  alignSelection,
  deleteSelection,
  distributeSelection,
  duplicateSelection,
  exportPackage,
  groupSelection,
  nudge,
  reorderSelection,
  selectAll,
  selectChildren,
  selectParent,
  toggleLocked,
  toggleVisible,
  ungroupSelection,
} from './state/actions.ts';
import { centreCut } from './model/snapping.ts';
import type { EditorStore } from './state/store.ts';

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export interface Shortcut {
  /** Display keys, "Mod" shown as ⌘ or Ctrl. */
  keys: string[];
  label: string;
  group: 'general' | 'create' | 'arrange' | 'view';
  match: (e: KeyboardEvent) => boolean;
  run: (store: EditorStore, e: KeyboardEvent) => void;
  /** Runs even when focus is in a text field. */
  inInputs?: boolean;
}

const mod = (e: KeyboardEvent) => (isMac ? e.metaKey : e.ctrlKey);
const plain = (e: KeyboardEvent) => !e.metaKey && !e.ctrlKey && !e.altKey;
const key = (e: KeyboardEvent, k: string) => e.key.toLowerCase() === k.toLowerCase();
/** Only 16:9 has a 4:3 centre cut; Shift+C does nothing on other shapes. */
const hasCentreCut = (s: EditorStore) => centreCut(s.scene.canvas.width, s.scene.canvas.height) !== undefined;
const code = (e: KeyboardEvent, c: string) => e.code === c;

export const SHORTCUTS: Shortcut[] = [
  { keys: ['Mod', 'Z'], label: 'shortcut.undo', group: 'general', match: (e) => mod(e) && !e.shiftKey && key(e, 'z'), run: (s) => s.undo() },
  { keys: ['Mod', 'Shift', 'Z'], label: 'shortcut.redo', group: 'general', match: (e) => (mod(e) && e.shiftKey && key(e, 'z')) || (mod(e) && !isMac && key(e, 'y')), run: (s) => s.redo() },
  { keys: ['Mod', 'S'], label: 'shortcut.save', group: 'general', match: (e) => mod(e) && key(e, 's'), run: (s) => void s.saveNow(), inInputs: true },
  { keys: ['Mod', 'C'], label: 'shortcut.copy', group: 'general', match: () => false, run: () => undefined },
  { keys: ['Mod', 'X'], label: 'shortcut.cut', group: 'general', match: () => false, run: () => undefined },
  { keys: ['Mod', 'V'], label: 'shortcut.paste', group: 'general', match: () => false, run: () => undefined },
  { keys: ['Mod', 'D'], label: 'shortcut.duplicate', group: 'general', match: (e) => mod(e) && key(e, 'd'), run: (s) => duplicateSelection(s) },
  { keys: ['Delete'], label: 'shortcut.delete', group: 'general', match: (e) => plain(e) && (e.key === 'Delete' || e.key === 'Backspace'), run: (s) => deleteSelection(s) },
  { keys: ['Mod', 'A'], label: 'shortcut.selectAll', group: 'general', match: (e) => mod(e) && key(e, 'a'), run: (s) => selectAll(s) },
  { keys: ['Esc'], label: 'shortcut.deselect', group: 'general', match: (e) => e.key === 'Escape', run: (s) => (s.state.ui.shortcutsOpen ? s.setUi({ shortcutsOpen: false }) : selectParent(s)) },
  { keys: ['Enter'], label: 'shortcut.selectChildren', group: 'general', match: (e) => plain(e) && !e.shiftKey && e.key === 'Enter', run: (s) => selectChildren(s) },
  { keys: ['Shift', 'Enter'], label: 'shortcut.selectParent', group: 'general', match: (e) => e.shiftKey && !mod(e) && e.key === 'Enter', run: (s) => selectParent(s) },
  { keys: ['Mod', 'E'], label: 'shortcut.export', group: 'general', match: (e) => mod(e) && !e.shiftKey && key(e, 'e'), run: (s) => void exportPackage(s) },
  { keys: ['?'], label: 'shortcut.help', group: 'general', match: (e) => e.key === '?' && !mod(e), run: (s) => s.setUi({ shortcutsOpen: !s.state.ui.shortcutsOpen }) },

  { keys: ['T'], label: 'shortcut.addText', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 't'), run: (s) => void addElement(s, 'text') },
  { keys: ['R'], label: 'shortcut.addRect', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 'r'), run: (s) => void addElement(s, 'rect') },
  { keys: ['O'], label: 'shortcut.addEllipse', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 'o'), run: (s) => void addElement(s, 'ellipse') },
  { keys: ['L'], label: 'shortcut.addLine', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 'l'), run: (s) => void addElement(s, 'line') },
  { keys: ['B'], label: 'shortcut.addBox', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 'b'), run: (s) => void addElement(s, 'box') },
  { keys: ['K'], label: 'shortcut.addStack', group: 'create', match: (e) => plain(e) && !e.shiftKey && key(e, 'k'), run: (s) => void addElement(s, 'stack') },

  { keys: ['Mod', 'G'], label: 'shortcut.group', group: 'arrange', match: (e) => mod(e) && !e.shiftKey && !e.altKey && key(e, 'g'), run: (s) => groupSelection(s) },
  { keys: ['Mod', 'Shift', 'G'], label: 'shortcut.ungroup', group: 'arrange', match: (e) => mod(e) && e.shiftKey && key(e, 'g'), run: (s) => ungroupSelection(s) },
  { keys: ['Mod', 'Alt', 'G'], label: 'shortcut.mask', group: 'arrange', match: (e) => mod(e) && e.altKey && !e.shiftKey && code(e, 'KeyG'), run: (s) => groupSelection(s, 'clipMask') },
  { keys: ['Mod', ']'], label: 'shortcut.forward', group: 'arrange', match: (e) => mod(e) && !e.shiftKey && code(e, 'BracketRight'), run: (s) => reorderSelection(s, 1) },
  { keys: ['Mod', '['], label: 'shortcut.backward', group: 'arrange', match: (e) => mod(e) && !e.shiftKey && code(e, 'BracketLeft'), run: (s) => reorderSelection(s, -1) },
  { keys: ['Mod', 'Shift', ']'], label: 'shortcut.front', group: 'arrange', match: (e) => mod(e) && e.shiftKey && code(e, 'BracketRight'), run: (s) => reorderSelection(s, 'front') },
  { keys: ['Mod', 'Shift', '['], label: 'shortcut.back', group: 'arrange', match: (e) => mod(e) && e.shiftKey && code(e, 'BracketLeft'), run: (s) => reorderSelection(s, 'back') },
  { keys: ['←', '↑', '→', '↓'], label: 'shortcut.nudge', group: 'arrange', match: (e) => !mod(e) && !e.altKey && !e.shiftKey && e.key.startsWith('Arrow'), run: (s, e) => arrow(s, e, 1) },
  { keys: ['Shift', '←↑→↓'], label: 'shortcut.nudge10', group: 'arrange', match: (e) => !mod(e) && !e.altKey && e.shiftKey && e.key.startsWith('Arrow'), run: (s, e) => arrow(s, e, 10) },
  // Handled by the timeline lanes and by tablists themselves; listed here for the "?" dialog.
  { keys: ['Enter'], label: 'shortcut.selectKey', group: 'arrange', match: () => false, run: () => undefined },
  { keys: ['Alt', '←', '→'], label: 'shortcut.moveKeys', group: 'arrange', match: () => false, run: () => undefined },
  { keys: ['←', '→'], label: 'shortcut.stepFrame', group: 'view', match: () => false, run: () => undefined },
  { keys: ['←', '→'], label: 'shortcut.tabs', group: 'view', match: () => false, run: () => undefined },
  { keys: ['Mod', 'Shift', 'H'], label: 'shortcut.hide', group: 'arrange', match: (e) => mod(e) && e.shiftKey && key(e, 'h'), run: (s) => toggleVisible(s) },
  { keys: ['Mod', 'Shift', 'L'], label: 'shortcut.lock', group: 'arrange', match: (e) => mod(e) && e.shiftKey && key(e, 'l'), run: (s) => toggleLocked(s) },
  { keys: ['Alt', 'A'], label: 'shortcut.alignLeft', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyA'), run: (s) => alignSelection(s, 'left') },
  { keys: ['Alt', 'H'], label: 'shortcut.alignHCenter', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyH'), run: (s) => alignSelection(s, 'hcenter') },
  { keys: ['Alt', 'D'], label: 'shortcut.alignRight', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyD'), run: (s) => alignSelection(s, 'right') },
  { keys: ['Alt', 'W'], label: 'shortcut.alignTop', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyW'), run: (s) => alignSelection(s, 'top') },
  { keys: ['Alt', 'V'], label: 'shortcut.alignVMiddle', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyV'), run: (s) => alignSelection(s, 'vmiddle') },
  { keys: ['Alt', 'S'], label: 'shortcut.alignBottom', group: 'arrange', match: (e) => e.altKey && !mod(e) && !e.shiftKey && code(e, 'KeyS'), run: (s) => alignSelection(s, 'bottom') },
  { keys: ['Alt', 'Shift', 'H'], label: 'shortcut.distributeH', group: 'arrange', match: (e) => e.altKey && e.shiftKey && !mod(e) && code(e, 'KeyH'), run: (s) => distributeSelection(s, 'x') },
  { keys: ['Alt', 'Shift', 'V'], label: 'shortcut.distributeV', group: 'arrange', match: (e) => e.altKey && e.shiftKey && !mod(e) && code(e, 'KeyV'), run: (s) => distributeSelection(s, 'y') },

  { keys: ['Shift', 'R'], label: 'shortcut.rulers', group: 'view', match: (e) => e.shiftKey && !mod(e) && !e.altKey && key(e, 'r'), run: (s) => s.setUi({ showRulers: !s.state.ui.showRulers }) },
  { keys: ['Shift', 'S'], label: 'shortcut.safe', group: 'view', match: (e) => e.shiftKey && !mod(e) && !e.altKey && key(e, 's'), run: (s) => s.setUi({ showSafe: !s.state.ui.showSafe }) },
  { keys: ['Shift', 'C'], label: 'shortcut.cut43', group: 'view', match: (e) => e.shiftKey && !mod(e) && !e.altKey && key(e, 'c'), run: (s) => hasCentreCut(s) && s.setUi({ showCut: !s.state.ui.showCut }) },
  { keys: ['Shift', 'G'], label: 'shortcut.guides', group: 'view', match: (e) => e.shiftKey && !mod(e) && !e.altKey && key(e, 'g'), run: (s) => s.setUi({ showGuides: !s.state.ui.showGuides }) },
  { keys: ['Shift', 'N'], label: 'shortcut.snapping', group: 'view', match: (e) => e.shiftKey && !mod(e) && !e.altKey && key(e, 'n'), run: (s) => s.setUi({ snapping: !s.state.ui.snapping }) },
  { keys: ['Mod', '0'], label: 'shortcut.zoomFit', group: 'view', match: (e) => mod(e) && (e.key === '0' || code(e, 'Digit0')), run: (s) => s.setUi({ zoom: 'fit' }) },
  { keys: ['Mod', '1'], label: 'shortcut.zoom100', group: 'view', match: (e) => mod(e) && (e.key === '1' || code(e, 'Digit1')), run: (s) => s.setUi({ zoom: 1 }) },
];

function arrow(store: EditorStore, e: KeyboardEvent, step: number): void {
  const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
  if (d) nudge(store, d[0]!, d[1]!);
}

export function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target as HTMLInputElement).type;
    return !['checkbox', 'radio', 'button', 'color', 'range', 'file'].includes(type);
  }
  return false;
}

/**
 * Whether a modal dialog is open (the save flow, the close prompt, a conflict, the preview...): the editor beneath
 * takes no shortcuts and no clipboard actions, whichever element has focus.
 */
export function modalOpen(): boolean {
  return typeof document !== 'undefined' && document.querySelector('[aria-modal="true"]') !== null;
}

/** Whether keys go to the canvas/tree: not while typing, and arrows not inside the layers tree (it navigates). */
export function handleKeydown(store: EditorStore, e: KeyboardEvent): void {
  if (modalOpen()) return;
  const editable = isEditable(e.target);
  const inTree = e.target instanceof HTMLElement && e.target.closest('[role="tree"]') !== null;
  // Widgets that use the arrow keys themselves (tabs, sliders, radio groups, listboxes, timeline keyframes).
  const ownArrows =
    e.target instanceof HTMLElement &&
    (e.target.closest('[role="tablist"], [role="slider"], [role="radiogroup"], [role="listbox"], [role="menu"], [data-own-arrows]') !== null ||
      (e.target instanceof HTMLInputElement && (e.target.type === 'range' || e.target.type === 'radio')));
  if (ownArrows && e.key.startsWith('Arrow')) return;
  // Enter and Space activate the focused control (a button, a link, a tab), never a canvas shortcut.
  const control = e.target instanceof HTMLElement && e.target.closest('button, a[href], summary, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="checkbox"], [role="switch"]') !== null;
  if (control && (e.key === 'Enter' || e.key === ' ')) return;
  for (const s of SHORTCUTS) {
    if (!s.match(e)) continue;
    if (editable && !s.inInputs && e.key !== 'Escape') return;
    if (editable && e.key === 'Escape') {
      (e.target as HTMLElement).blur();
      e.preventDefault();
      return;
    }
    if (inTree && (e.key.startsWith('Arrow') || e.key === 'Enter' || e.key === 'Delete' || e.key === 'Backspace') && !mod(e)) {
      if (e.key.startsWith('Arrow') || e.key === 'Enter') return;
    }
    e.preventDefault();
    s.run(store, e);
    return;
  }
}

export function displayKey(k: string): string {
  if (k === 'Mod') return isMac ? '⌘' : 'Ctrl';
  if (k === 'Alt') return isMac ? '⌥' : 'Alt';
  if (k === 'Shift') return isMac ? '⇧' : 'Shift';
  return k;
}
